/**
 * CataloguePage — /catalogue
 *
 * Renders the public Test catalogue in editorial density mode.
 * Track × Level filter grid (4 × 4 = 16 slots; empty slots show "Coming soon").
 * Test cards show: title, Track, Level, Time Limit, Pass Mark, Estimated Minutes.
 *
 * Per docs/design.md: editorial mode, cream background, cobalt accent, Space Grotesk.
 * Per issue 05-catalogue-browse/01 § Vertical slice scope.
 *
 * CARVE-OUT: uses raw fetch rather than a generated typed client because the
 * codegen pass (packages/web/src/api-client/) has not been wired yet
 * (Module 11 typed-client codegen is frontend-shell-dev's ongoing work).
 * This is documented in the issue Outcome § Cross-Module gaps.
 */

import {
  useCallback,
  useEffect,
  useState,
  type ReactElement,
} from 'react';
import { Link } from 'react-router-dom';
import { Shell } from '@/components/shell';
import { Button, Card } from '@/components/ui';
import { AvailabilityChip } from '@/components/availability/AvailabilityChip';
import { apiFetch } from '@/lib/api-client';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type Track = 'architecture' | 'mep' | 'structural' | 'interior';
type Level = 'novice' | 'intermediate' | 'advanced' | 'bim_champion';

const TRACKS: Track[] = ['architecture', 'mep', 'structural', 'interior'];
const LEVELS: Level[] = ['novice', 'intermediate', 'advanced', 'bim_champion'];

const TRACK_LABELS: Record<Track, string> = {
  architecture: 'Architecture',
  mep: 'MEP',
  structural: 'Structural',
  interior: 'Interior',
};

const LEVEL_LABELS: Record<Level, string> = {
  novice: 'Novice',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
  bim_champion: 'BIM Champion',
};

interface CatalogueTest {
  id: string;
  title: string;
  track: Track;
  level: Level;
  time_limit: number;
  pass_mark: number;
  estimated_minutes: number;
  status: string;
  /** J-X22 — availability window. Null/null = always-open. */
  availability_starts_at: string | null;
  availability_ends_at: string | null;
  availability_closed_reason: string | null;
}

interface CatalogueListResponse {
  ok: true;
  data: {
    items: CatalogueTest[];
    next_cursor: string | null;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Fetcher
// ─────────────────────────────────────────────────────────────────────────────

async function fetchCatalogue(params: {
  track?: Track;
  level?: Level;
  cursor?: string;
  limit?: number;
}): Promise<CatalogueListResponse['data']> {
  const qs = new URLSearchParams();
  if (params.track) qs.set('track', params.track);
  if (params.level) qs.set('level', params.level);
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  const res = await apiFetch(`/api/catalogue${qs.toString() ? `?${qs.toString()}` : ''}`);
  if (!res.ok) throw new Error(`catalogue fetch failed: ${String(res.status)}`);
  const body = (await res.json()) as CatalogueListResponse;
  return body.data;
}

// ─────────────────────────────────────────────────────────────────────────────
// Eyebrow component (matches landing.tsx pattern)
// ─────────────────────────────────────────────────────────────────────────────

function Eyebrow({ children }: { children: string }): ReactElement {
  return (
    <span className="inline-flex items-center gap-2 font-mono text-eyebrow font-medium uppercase text-muted">
      <span
        aria-hidden
        className="inline-block size-1.5 rounded-pill bg-accent"
        style={{ width: 6, height: 6 }}
      />
      {children}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TestCard
// ─────────────────────────────────────────────────────────────────────────────

function TestCard({ test }: { test: CatalogueTest }): ReactElement {
  return (
    <Link
      to={`/catalogue/${test.id}`}
      className="block no-underline"
    >
      <Card className="hover:shadow-card-hover">
        <p className="font-mono text-eyebrow font-medium uppercase text-muted mb-2">
          {TRACK_LABELS[test.track]} / {LEVEL_LABELS[test.level]}
        </p>
        <h3 className="font-sans text-h3 font-medium text-ink mb-4">{test.title}</h3>
        {/* J-X22 — availability chip. Renders nothing for always-open. */}
        <div className="mb-3">
          <AvailabilityChip
            input={{
              availability_starts_at: test.availability_starts_at,
              availability_ends_at: test.availability_ends_at,
              availability_closed_reason: test.availability_closed_reason,
            }}
          />
        </div>
        <dl className="grid grid-cols-3 gap-4 text-small text-muted">
          <div>
            <dt className="font-mono text-eyebrow uppercase text-muted-2 mb-1">Time</dt>
            <dd className="font-sans text-ink">{test.time_limit > 0 ? `${String(test.time_limit)} min` : 'No limit'}</dd>
          </div>
          <div>
            <dt className="font-mono text-eyebrow uppercase text-muted-2 mb-1">Pass mark</dt>
            <dd className="font-sans text-ink">{String(test.pass_mark)}%</dd>
          </div>
          <div>
            <dt className="font-mono text-eyebrow uppercase text-muted-2 mb-1">Est. time</dt>
            <dd className="font-sans text-ink">{String(test.estimated_minutes)} min</dd>
          </div>
        </dl>
      </Card>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ComingSoonSlot
// ─────────────────────────────────────────────────────────────────────────────

function ComingSoonSlot({ track, level }: { track: Track; level: Level }): ReactElement {
  return (
    <Card variant="warm" className="border-dashed text-center">
      <p className="font-mono text-eyebrow uppercase text-muted-2 mb-1">
        {TRACK_LABELS[track]} / {LEVEL_LABELS[level]}
      </p>
      <p className="font-sans text-small text-muted-2">Coming soon</p>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CataloguePage
// ─────────────────────────────────────────────────────────────────────────────

type LoadState = 'idle' | 'loading' | 'error';

export function CataloguePage(): ReactElement {
  const [filterTrack, setFilterTrack] = useState<Track | undefined>(undefined);
  const [filterLevel, setFilterLevel] = useState<Level | undefined>(undefined);
  const [tests, setTests] = useState<CatalogueTest[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [errorMsg, setErrorMsg] = useState<string>('');

  const loadTests = useCallback(
    async (opts: { reset?: boolean } = {}) => {
      setLoadState('loading');
      setErrorMsg('');
      try {
        const page = await fetchCatalogue({
          track: filterTrack,
          level: filterLevel,
          cursor: opts.reset ? undefined : (nextCursor ?? undefined),
        });
        setTests((prev) => (opts.reset ? page.items : [...prev, ...page.items]));
        setNextCursor(page.next_cursor);
        setLoadState('idle');
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : 'Failed to load catalogue');
        setLoadState('error');
      }
    },
    [filterTrack, filterLevel, nextCursor],
  );

  // Reset and reload when filters change
  useEffect(() => {
    setNextCursor(null);
    void (async () => {
      setLoadState('loading');
      setErrorMsg('');
      try {
        const page = await fetchCatalogue({ track: filterTrack, level: filterLevel });
        setTests(page.items);
        setNextCursor(page.next_cursor);
        setLoadState('idle');
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : 'Failed to load catalogue');
        setLoadState('error');
      }
    })();
  }, [filterTrack, filterLevel]);

  // Build a map from "track+level" → test for the grid
  const testMap = new Map<string, CatalogueTest>();
  for (const t of tests) {
    testMap.set(`${t.track}+${t.level}`, t);
  }

  return (
    <Shell mode='individual' densityMode='editorial'>
        {/* Header */}
        <Eyebrow>Catalogue</Eyebrow>
        <h1 className="mt-6 font-sans text-headline font-medium text-ink tracking-tight">
          BIM Assessment Catalogue
        </h1>
        <p className="mt-4 max-w-prose font-sans text-body-lg text-muted">
          Browse and filter BIM assessments by discipline and competency level.
        </p>

        {/* Filters */}
        <div className="mt-10 flex flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <label htmlFor="filter-track" className="font-mono text-eyebrow uppercase text-muted">
              Track
            </label>
            <select
              id="filter-track"
              value={filterTrack ?? ''}
              onChange={(e) => {
                const v = e.target.value as Track | '';
                setFilterTrack(v === '' ? undefined : v);
              }}
              className="rounded-md border border-line-2 bg-card px-3 py-2 font-sans text-small text-ink"
            >
              <option value="">All tracks</option>
              {TRACKS.map((t) => (
                <option key={t} value={t}>{TRACK_LABELS[t]}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="filter-level" className="font-mono text-eyebrow uppercase text-muted">
              Level
            </label>
            <select
              id="filter-level"
              value={filterLevel ?? ''}
              onChange={(e) => {
                const v = e.target.value as Level | '';
                setFilterLevel(v === '' ? undefined : v);
              }}
              className="rounded-md border border-line-2 bg-card px-3 py-2 font-sans text-small text-ink"
            >
              <option value="">All levels</option>
              {LEVELS.map((l) => (
                <option key={l} value={l}>{LEVEL_LABELS[l]}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Grid */}
        {loadState === 'error' && (
          <p className="mt-8 font-sans text-body text-error" role="alert">{errorMsg}</p>
        )}

        {loadState === 'loading' && tests.length === 0 && (
          <p className="mt-8 font-sans text-body text-muted">Loading…</p>
        )}

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* When no filter is active, render the full 4×4 grid */}
          {filterTrack === undefined && filterLevel === undefined ? (
            TRACKS.flatMap((track) =>
              LEVELS.map((level) => {
                const key = `${track}+${level}`;
                const test = testMap.get(key);
                return test ? (
                  <TestCard key={key} test={test} />
                ) : (
                  <ComingSoonSlot key={key} track={track} level={level} />
                );
              }),
            )
          ) : (
            tests.map((test) => <TestCard key={test.id} test={test} />)
          )}
        </div>

        {/* Load more */}
        {nextCursor !== null && (
          <div className="mt-8 flex justify-center">
            <Button
              variant='ghost'
              onClick={() => void loadTests()}
              disabled={loadState === 'loading'}
              className="rounded-md border border-line-2 bg-card px-6 py-3 font-sans text-small text-ink hover:bg-bg-warm disabled:opacity-50"
            >
              {loadState === 'loading' ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}
    </Shell>
  );
}
